import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Download, Plus, Trash2, Save, Package, ClipboardList, Tag, MapPin, Megaphone, Printer,
} from "lucide-react";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => `${Number(v).toFixed(2).replace(".", ",")} €`;

const STATUTS = ["nouvelle", "confirmee", "preparee", "retiree", "annulee"];
const STATUT_LABEL = { nouvelle: "Nouvelle", confirmee: "Confirmée", preparee: "Préparée", retiree: "Retirée", annulee: "Annulée" };
const CAMP_STATUTS = ["brouillon", "ouverte", "fermee", "terminee"];

const AdminCommandes = ({ token }) => {
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const [sub, setSub] = useState("commandes");
  const [campaigns, setCampaigns] = useState([]);
  const [points, setPoints] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // filters
  const [fCamp, setFCamp] = useState("");
  const [fPoint, setFPoint] = useState("");
  const [fStatut, setFStatut] = useState("");

  const loadBase = useCallback(async () => {
    try {
      const [c, pr] = await Promise.all([
        axios.get(`${API}/admin/campaigns`, h),
        axios.get(`${API}/admin/products`, h),
      ]);
      setCampaigns(c.data); setProducts(pr.data);
      const cid = c.data.find((x) => x.statut === "ouverte")?.id || c.data[0]?.id || "";
      const pts = await axios.get(`${API}/admin/points${cid ? `?campaign_id=${cid}` : ""}`, h);
      setPoints(pts.data);
    } catch (e) {
      if (e.response?.status === 401) toast.error("Session expirée.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (fCamp) q.set("campaign_id", fCamp);
      if (fPoint) q.set("point_id", fPoint);
      if (fStatut) q.set("statut", fStatut);
      const r = await axios.get(`${API}/admin/orders?${q.toString()}`, h);
      setOrders(r.data);
    } catch (e) { console.error("Chargement des commandes échoué:", e); toast.error("Impossible de charger les commandes."); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fCamp, fPoint, fStatut, token]);

  useEffect(() => { loadBase(); }, [loadBase]);
  useEffect(() => { loadOrders(); }, [loadOrders]);

  const setStatut = async (id, statut) => {
    await axios.put(`${API}/admin/orders/${id}/status`, { statut }, h);
    setOrders((o) => o.map((x) => (x.id === id ? { ...x, statut } : x)));
    toast.success("Statut mis à jour.");
  };

  const exportCsv = () => {
    const q = new URLSearchParams();
    if (fCamp) q.set("campaign_id", fCamp);
    if (fPoint) q.set("point_id", fPoint);
    if (fStatut) q.set("statut", fStatut);
    fetch(`${API}/admin/orders/export?${q.toString()}`, h)
      .then((r) => { if (!r.ok) throw new Error("export"); return r.blob(); })
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a"); a.href = url; a.download = "commandes_escargholala.csv"; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Export impossible. Reconnectez-vous."));
  };

  const SUBS = [
    ["commandes", "Commandes", ClipboardList],
    ["preparation", "Préparation", Package],
    ["produits", "Produits", Tag],
    ["points", "Points de retrait", MapPin],
    ["campagnes", "Campagnes", Megaphone],
  ];

  return (
    <div data-testid="admin-commandes">
      <div className="flex gap-2 flex-wrap mb-6">
        {SUBS.map(([k, label, Icon]) => (
          <button key={k} data-testid={`subtab-${k}`} onClick={() => setSub(k)}
            className={`rounded-full px-4 py-2 text-sm font-600 inline-flex items-center gap-2 transition-colors ${sub === k ? "bg-bordeaux text-white" : "bg-sable border border-border text-foreground hover:bg-white"}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {sub === "commandes" && (
        <div>
          <div className="flex flex-wrap gap-3 items-end mb-5">
            <FilterSelect label="Campagne" value={fCamp} onChange={setFCamp} options={[["", "Toutes"], ...campaigns.map((c) => [c.id, c.nom])]} testid="filter-campagne" />
            <FilterSelect label="Point" value={fPoint} onChange={setFPoint} options={[["", "Tous"], ...points.map((p) => [p.id, p.nom])]} testid="filter-point" />
            <FilterSelect label="Statut" value={fStatut} onChange={setFStatut} options={[["", "Tous"], ...STATUTS.map((s) => [s, STATUT_LABEL[s]])]} testid="filter-statut" />
            <button data-testid="orders-export" onClick={exportCsv} className="rounded-full border border-mousse text-mousse px-4 py-2 text-sm font-600 hover:bg-mousse hover:text-white transition-colors inline-flex items-center gap-2"><Download size={15} /> Export CSV</button>
          </div>
          {loading ? <Spin /> : orders.length === 0 ? <Empty text="Aucune commande." /> : (
            <div className="overflow-x-auto bg-sable rounded-2xl border border-border">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-foreground/60 border-b border-border">
                  {["N°", "Client", "Téléphone", "Retrait", "Date", "Montant", "Statut"].map((th) => <th key={th} className="px-4 py-3 font-600 whitespace-nowrap">{th}</th>)}
                </tr></thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} data-testid={`order-row-${o.numero}`} className="border-b border-border/60 last:border-0">
                      <td className="px-4 py-3 font-600 text-bordeaux whitespace-nowrap">{o.numero}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{o.client.prenom} {o.client.nom}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{o.client.telephone}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{o.point_nom}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{o.date_label}</td>
                      <td className="px-4 py-3 font-600 whitespace-nowrap">{fmt(o.total)}</td>
                      <td className="px-4 py-3">
                        <select data-testid={`order-status-${o.numero}`} value={o.statut} onChange={(e) => setStatut(o.id, e.target.value)} className="bg-white border border-border rounded-lg px-2 py-1 text-sm">
                          {STATUTS.map((s) => <option key={s} value={s}>{STATUT_LABEL[s]}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {sub === "preparation" && <Preparation token={token} campaigns={campaigns} />}
      {sub === "produits" && <Produits token={token} products={products} campaigns={campaigns} reload={loadBase} />}
      {sub === "points" && <Points token={token} campaigns={campaigns} />}
      {sub === "campagnes" && <Campagnes token={token} campaigns={campaigns} reload={loadBase} />}
    </div>
  );
};

const FilterSelect = ({ label, value, onChange, options, testid }) => (
  <div>
    <div className="text-xs text-foreground/55 mb-1">{label}</div>
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="bg-white border border-border rounded-lg px-3 py-2 text-sm min-w-[150px]">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </div>
);

const Spin = () => <div className="flex items-center justify-center py-16 text-foreground/50"><Loader2 className="animate-spin mr-2" /> Chargement...</div>;
const Empty = ({ text }) => <p className="text-foreground/50 text-center py-12">{text}</p>;

// ---------- Préparation ----------
const Preparation = ({ token, campaigns }) => {
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const [points, setPoints] = useState([]);
  const [pointId, setPointId] = useState("");
  const [dateId, setDateId] = useState("");
  const [prep, setPrep] = useState(null);
  const [loading, setLoading] = useState(false);
  const cid = campaigns.find((c) => c.statut === "ouverte")?.id || campaigns[0]?.id || "";

  useEffect(() => {
    if (!cid) return;
    axios.get(`${API}/admin/points?campaign_id=${cid}`, h).then((r) => setPoints(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cid]);

  const point = points.find((p) => p.id === pointId);
  const dateObj = point?.dates?.find((d) => d.id === dateId);

  const run = async (pid, did) => {
    if (!pid || !did) { setPrep(null); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/preparation?point_id=${pid}&date_id=${did}`, h);
      setPrep(r.data);
    } finally { setLoading(false); }
  };

  const printPrep = () => {
    if (!prep) return;
    const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const rowsQ = prep.produits.map((p) => `<tr><td>${esc(p.nom)} <span style="color:#888">(${esc(p.conditionnement)})</span></td><td style="text-align:right;font-weight:700">${p.quantite}</td></tr>`).join("");
    const rowsC = prep.commandes.map((c) => `<tr><td>${esc(c.numero)}</td><td>${esc(c.client)}</td><td>${esc(c.telephone)}</td><td style="text-align:right">${fmt(c.total)}</td><td>${esc(STATUT_LABEL[c.statut])}</td></tr>`).join("");
    const win = window.open("", "_blank");
    win.document.write(`<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Préparation ${point?.nom || ""} — ${dateObj?.label || ""}</title>
      <style>
        body{font-family:Georgia,serif;color:#2C2A28;padding:32px;max-width:800px;margin:0 auto}
        h1{color:#722F37;margin:0 0 4px} h2{color:#722F37;border-bottom:2px solid #722F37;padding-bottom:4px;margin-top:28px}
        .meta{color:#555;margin-bottom:16px} .kpi{display:flex;gap:24px;margin:16px 0}
        .kpi div{background:#F3EFE6;border-radius:8px;padding:12px 18px} .kpi b{display:block;font-size:24px;color:#722F37}
        table{width:100%;border-collapse:collapse;margin-top:8px} th,td{padding:7px 10px;border-bottom:1px solid #ddd;text-align:left;font-size:14px}
        th{background:#722F37;color:#fff} @media print{button{display:none}}
      </style></head><body>
      <h1>Feuille de préparation — Escarg'Holala</h1>
      <div class="meta"><strong>${esc(point?.nom || "")}</strong> ${point?.adresse ? "· " + esc(point.adresse) : ""}<br>${esc(dateObj?.label || "")} · ${esc(dateObj?.heure_debut || "")} – ${esc(dateObj?.heure_fin || "")}</div>
      <div class="kpi"><div>Commandes<b>${prep.nb_commandes}</b></div><div>Total à encaisser<b>${fmt(prep.total_a_encaisser)}</b></div></div>
      <h2>Quantités à préparer</h2><table><tbody>${rowsQ}</tbody></table>
      <h2>Commandes (${prep.nb_commandes})</h2><table><thead><tr><th>N°</th><th>Client</th><th>Téléphone</th><th style="text-align:right">Total</th><th>Statut</th></tr></thead><tbody>${rowsC}</tbody></table>
      <button onclick="window.print()" style="margin-top:24px;padding:10px 20px;background:#722F37;color:#fff;border:0;border-radius:20px;cursor:pointer;font-size:14px">Imprimer</button>
      </body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 400);
  };

  return (
    <div data-testid="prep-view">
      <div className="flex flex-wrap gap-3 items-end mb-5">
        <FilterSelect label="Point de retrait" value={pointId} onChange={(v) => { setPointId(v); setDateId(""); setPrep(null); }} options={[["", "—"], ...points.map((p) => [p.id, p.nom])]} testid="prep-point" />
        <FilterSelect label="Date" value={dateId} onChange={(v) => { setDateId(v); run(pointId, v); }} options={[["", "—"], ...((point?.dates || []).filter((d) => d.date_iso).map((d) => [d.id, d.label]))]} testid="prep-date" />
      </div>
      {loading ? <Spin /> : prep && (
        <div>
          <div className="flex justify-end mb-4">
            <button data-testid="prep-print" onClick={printPrep} className="rounded-full bg-bordeaux text-white px-5 py-2 text-sm font-600 inline-flex items-center gap-2 hover:bg-bordeaux-dark transition-colors"><Printer size={15} /> Imprimer la feuille</button>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <div className="bg-sable rounded-2xl border border-border p-5"><div className="text-3xl font-heading text-foreground" data-testid="prep-nb">{prep.nb_commandes}</div><div className="text-sm text-foreground/55">Commandes</div></div>
            <div className="bg-sable rounded-2xl border border-border p-5"><div className="text-3xl font-heading text-bordeaux" data-testid="prep-total">{fmt(prep.total_a_encaisser)}</div><div className="text-sm text-foreground/55">Total à encaisser</div></div>
          </div>
          <h3 className="font-heading text-xl text-foreground mb-3">Quantités à préparer</h3>
          <div className="bg-sable rounded-2xl border border-border overflow-hidden mb-6" data-testid="prep-quantities">
            {prep.produits.map((p, i) => (
              <div key={`${p.nom}-${p.conditionnement}`} className={`flex justify-between px-5 py-3 ${i !== 0 ? "border-t border-border/60" : ""}`}>
                <span className="text-foreground">{p.nom} <span className="text-foreground/50 text-sm">({p.conditionnement})</span></span>
                <span className="font-heading text-lg text-bordeaux">{p.quantite}</span>
              </div>
            ))}
          </div>
          <h3 className="font-heading text-xl text-foreground mb-3">Commandes</h3>
          <div className="overflow-x-auto bg-sable rounded-2xl border border-border">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-foreground/60 border-b border-border">{["N°", "Client", "Téléphone", "Total", "Statut"].map((t) => <th key={t} className="px-4 py-3 font-600">{t}</th>)}</tr></thead>
              <tbody>{prep.commandes.map((c) => (
                <tr key={c.numero} className="border-b border-border/60 last:border-0">
                  <td className="px-4 py-3 font-600 text-bordeaux">{c.numero}</td><td className="px-4 py-3">{c.client}</td><td className="px-4 py-3">{c.telephone}</td><td className="px-4 py-3 font-600">{fmt(c.total)}</td><td className="px-4 py-3">{STATUT_LABEL[c.statut]}</td>
                </tr>))}</tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------- Produits ----------
const Produits = ({ token, products, campaigns, reload }) => {
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const [list, setList] = useState(products);
  useEffect(() => setList(products), [products]);
  const upd = (id, patch) => setList((l) => l.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  const save = async (p) => {
    await axios.put(`${API}/admin/products/${p.id}`, { nom: p.nom, conditionnement: p.conditionnement, prix: parseFloat(String(p.prix).replace(",", ".")) || 0, categorie: p.categorie, actif: p.actif, campaign_ids: p.campaign_ids || [] }, h);
    toast.success("Produit enregistré.");
  };
  const del = async (id) => { if (!window.confirm("Supprimer ce produit ?")) return; await axios.delete(`${API}/admin/products/${id}`, h); reload(); toast.success("Supprimé."); };
  const add = async () => { await axios.post(`${API}/admin/products`, { nom: "Nouveau produit", conditionnement: "", prix: 0, categorie: "", actif: true, campaign_ids: campaigns.map((c) => c.id), ordre: 999 }, h); reload(); };
  const toggleCamp = (p, cid) => { const has = (p.campaign_ids || []).includes(cid); upd(p.id, { campaign_ids: has ? p.campaign_ids.filter((x) => x !== cid) : [...(p.campaign_ids || []), cid] }); };

  return (
    <div data-testid="produits-admin">
      <div className="flex justify-end mb-4"><button data-testid="add-product" onClick={add} className="rounded-full bg-bordeaux text-white px-4 py-2 text-sm font-600 inline-flex items-center gap-2 hover:bg-bordeaux-dark"><Plus size={15} /> Ajouter un produit</button></div>
      <div className="space-y-3">
        {list.map((p) => (
          <div key={p.id} data-testid={`product-admin-${p.id}`} className="bg-sable rounded-2xl border border-border p-4">
            <div className="grid sm:grid-cols-12 gap-3 items-center">
              <Input className="bg-white sm:col-span-3" value={p.nom} onChange={(e) => upd(p.id, { nom: e.target.value })} placeholder="Nom" />
              <Input className="bg-white sm:col-span-3" value={p.conditionnement || ""} onChange={(e) => upd(p.id, { conditionnement: e.target.value })} placeholder="Conditionnement" />
              <Input className="bg-white sm:col-span-2" value={p.prix} onChange={(e) => upd(p.id, { prix: e.target.value })} placeholder="Prix €" />
              <Input className="bg-white sm:col-span-2" value={p.categorie || ""} onChange={(e) => upd(p.id, { categorie: e.target.value })} placeholder="Catégorie" />
              <div className="sm:col-span-2 flex items-center justify-end gap-2">
                <label className="flex items-center gap-1 text-xs text-foreground/70"><input type="checkbox" checked={!!p.actif} onChange={(e) => upd(p.id, { actif: e.target.checked })} className="accent-bordeaux" /> Actif</label>
                <button onClick={() => save(p)} className="w-8 h-8 rounded-lg bg-mousse text-white flex items-center justify-center hover:opacity-90" title="Enregistrer"><Save size={15} /></button>
                <button onClick={() => del(p.id)} className="w-8 h-8 rounded-lg border border-border text-bordeaux flex items-center justify-center hover:bg-white" title="Supprimer"><Trash2 size={15} /></button>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-border/60">
              {campaigns.map((c) => (
                <label key={c.id} className="flex items-center gap-1.5 text-xs text-foreground/70"><input type="checkbox" checked={(p.campaign_ids || []).includes(c.id)} onChange={() => toggleCamp(p, c.id)} className="accent-bordeaux" /> {c.nom}</label>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- Points de retrait ----------
const Points = ({ token, campaigns }) => {
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const [cid, setCid] = useState(campaigns.find((c) => c.statut === "ouverte")?.id || campaigns[0]?.id || "");
  const [list, setList] = useState([]);
  const load = useCallback(() => { if (cid) axios.get(`${API}/admin/points?campaign_id=${cid}`, h).then((r) => setList(r.data)); /* eslint-disable-next-line */ }, [cid, token]);
  useEffect(() => { load(); }, [load]);
  const upd = (id, patch) => setList((l) => l.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  const save = async (p) => { await axios.put(`${API}/admin/points/${p.id}`, { nom: p.nom, adresse: p.adresse, infos: p.infos, presentation: p.presentation, image: p.image, site_url: p.site_url, actif: p.actif, dates: p.dates }, h); toast.success("Point enregistré."); load(); };
  const del = async (id) => { if (!window.confirm("Supprimer ce point ?")) return; await axios.delete(`${API}/admin/points/${id}`, h); load(); };
  const add = async () => { await axios.post(`${API}/admin/points`, { campaign_id: cid, nom: "Nouveau point", adresse: "", infos: "", actif: true, dates: [], ordre: 999 }, h); load(); };

  const addDate = (p) => upd(p.id, { dates: [...(p.dates || []), { date_iso: "", heure_debut: "16:00", heure_fin: "20:00", capacity_enabled: false, capacite_max: 0, ferme: false }] });
  const updDate = (p, i, patch) => upd(p.id, { dates: p.dates.map((d, di) => (di === i ? { ...d, ...patch } : d)) });
  const delDate = (p, i) => upd(p.id, { dates: p.dates.filter((_, di) => di !== i) });

  return (
    <div data-testid="points-admin">
      <div className="flex justify-between items-end mb-4 flex-wrap gap-3">
        <FilterSelect label="Campagne" value={cid} onChange={setCid} options={campaigns.map((c) => [c.id, c.nom])} testid="points-campaign" />
        <button data-testid="add-point" onClick={add} className="rounded-full bg-bordeaux text-white px-4 py-2 text-sm font-600 inline-flex items-center gap-2 hover:bg-bordeaux-dark"><Plus size={15} /> Ajouter un point</button>
      </div>
      <div className="space-y-4">
        {list.map((p) => (
          <div key={p.id} data-testid={`point-admin-${p.id}`} className="bg-sable rounded-2xl border border-border p-5">
            <div className="grid sm:grid-cols-12 gap-3 items-center">
              <Input className="bg-white sm:col-span-4" value={p.nom} onChange={(e) => upd(p.id, { nom: e.target.value })} placeholder="Nom du point" />
              <Input className="bg-white sm:col-span-4" value={p.adresse || ""} onChange={(e) => upd(p.id, { adresse: e.target.value })} placeholder="Adresse" />
              <Input className="bg-white sm:col-span-2" value={p.infos || ""} onChange={(e) => upd(p.id, { infos: e.target.value })} placeholder="Infos" />
              <div className="sm:col-span-2 flex items-center justify-end gap-2">
                <label className="flex items-center gap-1 text-xs text-foreground/70"><input type="checkbox" checked={!!p.actif} onChange={(e) => upd(p.id, { actif: e.target.checked })} className="accent-bordeaux" /> Actif</label>
                <button onClick={() => del(p.id)} className="w-8 h-8 rounded-lg border border-border text-bordeaux flex items-center justify-center hover:bg-white"><Trash2 size={15} /></button>
              </div>
            </div>
            <div className="grid sm:grid-cols-12 gap-3 mt-3">
              <Input className="bg-white sm:col-span-6" value={p.image || ""} onChange={(e) => upd(p.id, { image: e.target.value })} placeholder="URL de la photo du domaine" />
              <Input className="bg-white sm:col-span-6" value={p.site_url || ""} onChange={(e) => upd(p.id, { site_url: e.target.value })} placeholder="Site internet (https://...)" />
              <textarea className="bg-white sm:col-span-12 rounded-md border border-input px-3 py-2 text-sm min-h-[80px]" value={p.presentation || ""} onChange={(e) => upd(p.id, { presentation: e.target.value })} placeholder="Présentation du domaine qui vous accueille (affichée au client sur l'étape retrait)" />
            </div>
            <div className="mt-4 pt-3 border-t border-border/60">
              <div className="flex items-center justify-between mb-2"><span className="text-sm font-600 text-foreground/70">Dates de retrait</span><button onClick={() => addDate(p)} className="text-xs text-mousse font-600 inline-flex items-center gap-1 hover:underline"><Plus size={13} /> Ajouter une date</button></div>
              {(p.dates || []).length === 0 && <p className="text-xs text-foreground/45 italic">Aucune date — le point ne sera pas visible par les clients.</p>}
              {(p.dates || []).map((d, i) => (
                <div key={d.id || i} className="grid sm:grid-cols-12 gap-2 items-center mb-2">
                  <Input type="date" className="bg-white sm:col-span-3" value={d.date_iso || ""} onChange={(e) => updDate(p, i, { date_iso: e.target.value })} />
                  <Input type="time" className="bg-white sm:col-span-2" value={d.heure_debut || ""} onChange={(e) => updDate(p, i, { heure_debut: e.target.value })} />
                  <Input type="time" className="bg-white sm:col-span-2" value={d.heure_fin || ""} onChange={(e) => updDate(p, i, { heure_fin: e.target.value })} />
                  <label className="sm:col-span-2 flex items-center gap-1 text-xs text-foreground/70"><input type="checkbox" checked={!!d.capacity_enabled} onChange={(e) => updDate(p, i, { capacity_enabled: e.target.checked })} className="accent-bordeaux" /> Max</label>
                  <Input type="number" className="bg-white sm:col-span-2" value={d.capacite_max || 0} onChange={(e) => updDate(p, i, { capacite_max: parseInt(e.target.value) || 0 })} placeholder="0" />
                  <div className="sm:col-span-1 flex items-center gap-1 justify-end">
                    <label className="flex items-center gap-1 text-xs text-foreground/70" title="Fermer cette date"><input type="checkbox" checked={!!d.ferme} onChange={(e) => updDate(p, i, { ferme: e.target.checked })} className="accent-bordeaux" /></label>
                    <button onClick={() => delDate(p, i)} className="text-bordeaux"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
              <button onClick={() => save(p)} className="mt-2 rounded-full bg-mousse text-white px-4 py-2 text-sm font-600 inline-flex items-center gap-2 hover:opacity-90"><Save size={14} /> Enregistrer ce point</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------- Campagnes ----------
const Campagnes = ({ token, campaigns, reload }) => {
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const [list, setList] = useState(campaigns);
  useEffect(() => setList(campaigns), [campaigns]);
  const upd = (id, patch) => setList((l) => l.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const save = async (c) => { await axios.put(`${API}/admin/campaigns/${c.id}`, { nom: c.nom, description: c.description, date_debut: c.date_debut, date_fin: c.date_fin, statut: c.statut }, h); toast.success("Campagne enregistrée."); reload(); };
  const del = async (id) => { if (!window.confirm("Supprimer cette campagne ?")) return; await axios.delete(`${API}/admin/campaigns/${id}`, h); reload(); };
  const add = async () => { await axios.post(`${API}/admin/campaigns`, { nom: "Nouvelle campagne", description: "", statut: "brouillon" }, h); reload(); };

  return (
    <div data-testid="campagnes-admin">
      <div className="flex justify-end mb-4"><button data-testid="add-campaign" onClick={add} className="rounded-full bg-bordeaux text-white px-4 py-2 text-sm font-600 inline-flex items-center gap-2 hover:bg-bordeaux-dark"><Plus size={15} /> Nouvelle campagne</button></div>
      <div className="space-y-3">
        {list.map((c) => (
          <div key={c.id} data-testid={`campaign-admin-${c.id}`} className="bg-sable rounded-2xl border border-border p-4">
            <div className="grid sm:grid-cols-12 gap-3 items-center">
              <Input className="bg-white sm:col-span-4" value={c.nom} onChange={(e) => upd(c.id, { nom: e.target.value })} placeholder="Nom" />
              <Input className="bg-white sm:col-span-3" value={c.description || ""} onChange={(e) => upd(c.id, { description: e.target.value })} placeholder="Description" />
              <Input type="date" className="bg-white sm:col-span-2" value={c.date_debut || ""} onChange={(e) => upd(c.id, { date_debut: e.target.value })} />
              <div className="sm:col-span-2">
                <select value={c.statut} onChange={(e) => upd(c.id, { statut: e.target.value })} className="bg-white border border-border rounded-lg px-2 py-2 text-sm w-full">
                  {CAMP_STATUTS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="sm:col-span-1 flex items-center gap-2 justify-end">
                <button onClick={() => save(c)} className="w-8 h-8 rounded-lg bg-mousse text-white flex items-center justify-center"><Save size={15} /></button>
                <button onClick={() => del(c.id)} className="w-8 h-8 rounded-lg border border-border text-bordeaux flex items-center justify-center hover:bg-white"><Trash2 size={15} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminCommandes;
